import { matchPath } from 'react-router'

/**
 * The property routes' tabs, in display order (CAP-1034). Every tab but
 * Messages is a `?tab=` value on `/properties/:id`; Messages is its own route,
 * `/properties/:id/messages`. Both routes render the same tab row from this
 * list, so moving between them keeps it in place.
 */
export const PROPERTY_PAGE_TABS = [
  { value: 'calendar', label: 'Calendar' },
  { value: 'ask', label: 'Ask' },
  { value: 'messages', label: 'Messages' },
  { value: 'reviews', label: 'Reviews' },
  { value: 'weekly-emails', label: 'Weekly Emails' },
  { value: 'settings', label: 'Settings' },
] as const

export type PropertyPageTabValue = (typeof PROPERTY_PAGE_TABS)[number]['value']

function isPropertyPageTab(value: string): value is PropertyPageTabValue {
  return PROPERTY_PAGE_TABS.some(tab => tab.value === value)
}

/** Where a tab lives: the property page with its `?tab=`, or the Messages route. */
export function propertyTabPath(propertyId: string, tab: string): string {
  const id = encodeURIComponent(propertyId)
  if (tab === 'messages') return `/properties/${id}/messages`
  // Calendar is the property page's default, so its canonical URL has no tab.
  if (tab === 'calendar' || !isPropertyPageTab(tab)) return `/properties/${id}`
  return `/properties/${id}?tab=${tab}`
}

/**
 * Where switching to `nextPropertyId` lands from the current location: the
 * same tab of the property page (`?tab=` kept), or the new property's
 * Messages list. A thread deep link falls back to that list, since a thread
 * belongs to one property.
 */
export function propertySwitchPath(pathname: string, search: string, nextPropertyId: string) {
  if (
    matchPath('/properties/:propertyId/messages', pathname) ||
    matchPath('/properties/:propertyId/messages/:threadId', pathname)
  ) {
    return propertyTabPath(nextPropertyId, 'messages')
  }
  // Through propertyTabPath, so `?tab=calendar` or an unknown tab lands on
  // the canonical tab-less URL.
  return propertyTabPath(nextPropertyId, new URLSearchParams(search).get('tab') ?? 'calendar')
}
